import { STUDY_STATUS } from '@/entities/study';
import { ECGClient, ECGViewer } from '@/widgets/ecg-viewer';
import { closeIcon, inforOrangeIcon, noDataYetIcon } from '@/shared/ui/assets';
import { CustomButton } from '@/shared/ui/components';
import _ from 'lodash';
import Skeleton from 'react-loading-skeleton';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import FullEventChart from './events/eventDetails/eventDetailsTab/fullEventChart';
import EventParameters from './events/eventDetails/eventDetailsTab/eventParameters/eventParameters';
import './style.scss';

import { generateTitleBlockArray } from '../../lib/eventECGViewer';
import { useEventECGViewer } from './useEventECGViewer';
import type { EventECGViewerProps } from './eventECGViewer.types';

/**
 * Displays the ECG strip and title details of a selected study event, warning that event classification may change on final review. Renders a skeleton until the title info loads, shows a no-strip message whose wording depends on whether the study is DONE when state.isNoEcgData is set, otherwise renders the full-event chart and the ECG viewer, and returns to the event list through onClickClose.
 *
 * @param data - The event plus its study context used to load the strip.
 * @param handleClickBack - Callback invoked when the viewer is closed.
 * @returns The event ECG viewer markup.
 */
const EventECGViewer = ({ data, handleClickBack }: EventECGViewerProps) => {
  const { state, titleInfo, onClickClose, toggleDisplayViewFullEventChart, updateChartData } =
    useEventECGViewer({ data, handleClickBack });

  return (
    <div className="event-ecg-viewer">
      <div className="event-ecg-viewer__title">
        {!_.isEmpty(titleInfo.current) ? (
          <div className="title-block-container">
            {_.map(generateTitleBlockArray(titleInfo.current), (element, index) => (
              <div key={index} className="title-block">
                <div className="title-block__title">{element.title}</div>

                <div className="flex items-center">
                  <div className="title-block__content">{element.content}</div>
                </div>
              </div>
            ))}
          </div>
        ) : state.isNoEcgData ? null : (
          <div className="title-skeleton-container">
            <Skeleton height="100%" />
          </div>
        )}

        <CustomButton
          style="neutral"
          size="small"
          type="outline"
          id="close-event-ecg-btn"
          icon={<ReactSVG src={closeIcon} />}
          onClick={onClickClose}
          ariaLabel={t`Close`}
        />
      </div>
      <div className="warning-message">
        <img src={inforOrangeIcon} alt="inforOrangeIcon" />
        <span>
          {t`The following event(s) may or may not have been finalized and their classification may change upon final review`}
        </span>
      </div>
      {state.isNoEcgData ? (
        <div className="event-ecg-viewer__no-data">
          <ReactSVG src={noDataYetIcon} aria-hidden="true" />
          <div className="no-data__text">
            {data?.study?.status === STUDY_STATUS.DONE
              ? t`No strip is available for this event. This can happen if the patient wasn't wearing the device or it was being charged during the event.`
              : t`The strip is not available yet. The data may still be uploading. Please check again after the study is complete and the device has finished uploading.`}
          </div>
        </div>
      ) : (
        <div className="event-ecg-viewer__body">
          {state.isShowViewFullEventChart && (
            <FullEventChart
              viewFullEventData={state.viewFullEventData}
              updateChartData={updateChartData}
            />
          )}
          {state.isLoadedData && !!state.ecgObject.dataSamples?.length ? (
            <ECGClient>
              <ECGViewer
                isLoadedData={state.isLoadedData}
                ecg={state.ecgObject}
                configChart={state.configChart}
                startStudyDate={data.startStudyDate}
                facilityCountry={data.facilityCountry}
                facilityTimezone={data.facilityTimezone}
                timeObject={state.timeObject}
              />
            </ECGClient>
          ) : (
            <div className="ecg-viewer-skeleton-container">
              <div className="chart-container">
                <Skeleton height="100%" />
              </div>
              <div className="menu-container">
                <Skeleton height="100%" />
              </div>
            </div>
          )}
          <EventParameters
            isLoadedData={state.isLoadedData}
            eventDuration={state.eventDuration}
            maxHr={state.maxHr}
            minHr={state.minHr}
            isShowViewFullEventButton={state.isShowViewFullEventButton}
            isViewFullEventAvailable={state.isViewFullEventAvailable}
            isShowViewFullEventChart={state.isShowViewFullEventChart}
            onClickViewFullEvent={toggleDisplayViewFullEventChart}
            isNotEditHr
          />
        </div>
      )}
    </div>
  );
};

export default EventECGViewer;
